import React, { useState } from 'react';
import { ArrowLeft, Mail, User, Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { passwordResetService } from '../services/api';
import { useNavigate } from 'react-router-dom';

const ForgotPassword = () => {
  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!username.trim()) {
      setResult({
        type: 'error',
        message: 'Veuillez saisir votre nom d\'utilisateur'
      });
      return;
    }

    try {
      setLoading(true);
      setResult(null);
      
      const response = await passwordResetService.requestReset(username.trim());
      
      if (response.contact_admin) {
        setResult({
          type: 'warning',
          message: response.message,
          adminMessage: response.admin_message
        });
      } else {
        setResult({
          type: 'success',
          message: response.message
        });
      }
    } catch (error) {
      console.error('Error requesting password reset:', error);
      
      const errorMessage = error.response?.data?.detail || "Erreur lors de la demande de réinitialisation";
      setResult({
        type: 'error',
        message: errorMessage
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold text-gray-800 flex items-center gap-2 justify-center">
            <Mail className="w-6 h-6 text-blue-600" />
            Mot de passe oublié
          </CardTitle>
          <CardDescription>
            Entrez votre nom d'utilisateur pour recevoir un lien de réinitialisation
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {result && (
            <div className={`p-4 rounded-lg border ${
              result.type === 'success' ? 'bg-green-50 border-green-200' :
              result.type === 'warning' ? 'bg-orange-50 border-orange-200' :
              'bg-red-50 border-red-200'
            }`}>
              <div className="flex items-start gap-3">
                {result.type === 'success' && <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />}
                {result.type === 'warning' && <AlertCircle className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />}
                {result.type === 'error' && <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />}
                
                <div className="flex-1">
                  <p className={`text-sm font-medium ${
                    result.type === 'success' ? 'text-green-800' :
                    result.type === 'warning' ? 'text-orange-800' :
                    'text-red-800'
                  }`}>
                    {result.message}
                  </p>
                  
                  {result.adminMessage && (
                    <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded">
                      <p className="text-sm text-blue-800">
                        <strong>💡 Solution :</strong> {result.adminMessage}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-sm font-medium text-gray-700">
                Nom d'utilisateur *
              </Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Votre nom d'utilisateur"
                  className="pl-10"
                  required
                  disabled={loading}
                />
              </div>
              <p className="text-xs text-gray-500">
                Exemple : TCCC, TPPayerne, etc.
              </p>
            </div>

            <Button 
              type="submit" 
              className="w-full" 
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Envoi en cours...
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  Envoyer le lien de réinitialisation
                </>
              )}
            </Button>
          </form>

          <div className="text-center">
            <Button 
              variant="ghost" 
              onClick={handleBackToLogin}
              className="text-sm text-gray-600 hover:text-gray-800"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour à la connexion
            </Button>
          </div>

          {/* Info section */}
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="text-sm font-medium text-blue-800 mb-2">Comment ça fonctionne ?</h4>
            <ul className="text-xs text-blue-700 space-y-1">
              <li>• Nous vérifions si votre compte a un email configuré</li>
              <li>• Si oui, nous envoyons un lien de réinitialisation</li>
              <li>• Si non, contactez l'administrateur</li>
              <li>• Le lien expire dans 24 heures</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPassword;